import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ReceiptText, RefreshCw, FileText, ExternalLink } from "lucide-react";
import { parasutApi, errMsg, openInvoicePdf } from "../lib/parasutApi";
import { DOC_LABEL, STATUS, money } from "../components/invoice/invoiceDefaults";
import { StatusBadge } from "../components/invoice/InvoiceHistory";
import { InvoiceDialog } from "../components/invoice/InvoiceDialog";
import { InvoiceRules } from "../components/invoice/InvoiceRules";
import { BillableList } from "../components/invoice/BillableList";

const iso = (d) => d.toISOString().slice(0, 10);
const inputCls = "h-9 px-3 rounded-lg border border-slate-300 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/40";

function ConnectionChip() {
  const [st, setSt] = useState(null);
  useEffect(() => { parasutApi.status().then(setSt).catch((e) => setSt({ error: errMsg(e) })); }, []);
  if (!st) return <span className="text-xs text-slate-400">Paraşüt kontrol ediliyor…</span>;
  return st.error
    ? <span data-testid="parasut-status" className="text-xs font-semibold text-red-600">Paraşüt bağlantı hatası: {st.error}</span>
    : <span data-testid="parasut-status" className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Paraşüt bağlı · {st.company_name}</span>;
}

function SummaryCards({ rows }) {
  const live = rows.filter((r) => r.status === "approved");
  const byCur = live.reduce((a, r) => ({ ...a, [r.currency]: (a[r.currency] || 0) + Number(r.gross_total || 0) }), {});
  const card = (label, value, testId) => (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div data-testid={testId} className="text-xl font-bold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {card("Onaylı fatura", live.length, "inv-sum-approved")}
      {card("Taslak / hatalı", rows.filter((r) => ["draft", "error", "failed"].includes(r.status)).length, "inv-sum-draft")}
      {card("İptal", rows.filter((r) => r.status === "cancelled").length, "inv-sum-cancelled")}
      {card("Onaylı toplam", Object.entries(byCur).map(([c, v]) => money(v, c)).join(" · ") || "—", "inv-sum-total")}
    </div>
  );
}

export default function Invoices() {
  const [from, setFrom] = useState(iso(new Date(Date.now() - 6 * 864e5)));
  const [to, setTo] = useState(iso(new Date()));
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openBooking, setOpenBooking] = useState(null);
  const [tab, setTab] = useState("billable");

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await parasutApi.list({ date_from: from, date_to: to, status: status || undefined })); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [from, to, status]);
  useEffect(() => { load(); }, [load]);

  const pdf = async (r) => {
    try { if (!(await openInvoicePdf(r.id))) toast.info("PDF hazırlanıyor, birazdan tekrar deneyin"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="invoices-page" className="min-h-full bg-slate-50 px-6 py-6 flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-4">
        <div className="mr-auto">
          <h1 className="flex items-center gap-2 text-3xl font-bold text-slate-900"><ReceiptText className="text-indigo-600" /> Faturalar</h1>
          <p className="text-sm text-slate-500 mt-1">Canlı Sayfa'dan kesilen Paraşüt faturaları (tur tarihine göre). <ConnectionChip /></p>
          <div className="mt-3 inline-flex rounded-full border border-slate-300 bg-white p-0.5">
            {[["billable", "Faturalanacaklar"], ["list", "Kesilen Faturalar"], ["rules", "Fatura Ayarları"]].map(([k, l]) => (
              <button key={k} data-testid={`inv-tab-${k}`} onClick={() => setTab(k)}
                className={`px-4 h-8 rounded-full text-sm font-semibold transition-colors ${tab === k ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
            ))}
          </div>
        </div>
        {tab === "list" && (<>
        <input data-testid="inv-filter-from" type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} />
        <input data-testid="inv-filter-to" type="date" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} />
        <select data-testid="inv-filter-status" className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Tüm durumlar</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button data-testid="inv-reload" onClick={load} className="h-9 px-4 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors inline-flex items-center gap-1.5">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Yenile
        </button>
        </>)}
      </div>
      {tab === "rules" ? <InvoiceRules /> : tab === "billable" ? <BillableList /> : (<>
      <SummaryCards rows={rows} />
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm text-slate-700">
          <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>{["Tur tarihi", "Kod", "Müşteri", "Ülke", "Telefon", "Belge", "Fatura no", "Tutar", "Durum", "Kesen", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody data-testid="inv-tbody">
            {rows.length === 0 && <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-400">{loading ? "Yükleniyor…" : "Bu aralıkta fatura yok. Canlı Sayfa'da bir rezervasyon seçip “Fatura Kes”e basın."}</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`inv-row-${r.id}`} className="border-t border-slate-100 hover:bg-indigo-50/40 transition-colors">
                <td className="px-3 py-2 tabular-nums">{r.booking_date}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.confirmation_code}</td>
                <td className="px-3 py-2">{r.customer_name}</td>
                <td data-testid={`inv-row-country-${r.id}`} className="px-3 py-2 text-xs">{r.country || "—"}</td>
                <td data-testid={`inv-row-phone-${r.id}`} className="px-3 py-2 text-xs tabular-nums">{r.phone || "—"}</td>
                <td className="px-3 py-2">{DOC_LABEL[r.doc_type] || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.invoice_no || "—"}</td>
                <td className="px-3 py-2 tabular-nums font-semibold">{money(r.gross_total, r.currency)}</td>
                <td className="px-3 py-2"><StatusBadge status={r.status} testId={`inv-row-status-${r.id}`} />{r.error && <div className="text-[11px] text-red-600 max-w-xs truncate" title={r.error}>{r.error}</div>}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.created_by}</td>
                <td className="px-3 py-2 whitespace-nowrap text-right">
                  {r.status === "approved" && <button data-testid={`inv-row-pdf-${r.id}`} onClick={() => pdf(r)} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline mr-3"><FileText size={13} /> PDF</button>}
                  <button data-testid={`inv-row-open-${r.id}`} onClick={() => setOpenBooking(r.booking_id)} className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:underline"><ExternalLink size={13} /> Aç</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>)}
      {openBooking && <InvoiceDialog bookingId={openBooking} open onOpenChange={(o) => { if (!o) { setOpenBooking(null); load(); } }} />}
    </div>
  );
}
